import React, { useEffect, useState, useMemo } from 'react'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import { superAxiosRequest } from 'axiosApi'
import styles from './CustomerTrafficReport.scss'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { ExcelRowsParams } from 'components/utils/ExcelExport'
import { timeConverter } from 'components/ContentBox/Online-schedule/components/functions'
import { observer } from 'mobx-react'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'

const cols = [
  { name: 'Дата' },
  { name: 'На начало дня' },
  { name: 'Прибыло' },
  { name: 'Убыло' },
  { name: 'На конец дня' },
]

const CustomerTrafficReport = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const { refWrapper, refThead, data, setData, isLoading, setIsLoading } =
    useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
    },
  })

  const filterCnf = useMemo(() => {
    return [
      {
        type: 'select-multi',
        label: 'Локация',
        placeholder: 'Локация',
        nameState: 'location_ids',
        values: catalog?.locations,
      },
      {
        type: 'date_day_picker',
        nameState: ['date_from', 'date_until'],
        label: 'Период',
        isRequired: true,
      },
    ]
  }, [catalog])

  useEffect(() => {
    setIsLoading(true)
    const requestParams = {
      ...filter,
    }

    if (requestParams.location_ids && requestParams.location_ids.length > 0)
      requestParams.location_ids = `[${requestParams.location_ids.map((item) => item)}]`
    const aggregateDataByDate = (data) => {
      const aggregatedData = {}

      Object.entries(data).forEach(([_, dates]) => {
        Object.entries(dates).forEach(([date, { at_start, joined, left }]) => {
          if (!aggregatedData[date]) {
            aggregatedData[date] = { at_start: 0, joined: 0, left: 0 }
          }
          // Суммируем значения для одинаковых дат
          aggregatedData[date].at_start += at_start
          aggregatedData[date].joined += joined
          aggregatedData[date].left += left
        })
      })

      return aggregatedData
    }

    superAxiosRequest({
      method: 'get',
      link: 'groups/reports/student_dynamics_second',
      params: requestParams,
    })
      .then((response) => {
        setIsLoading(false)
        setCatalog(response.data)
        const aggregatedData = aggregateDataByDate(response.data.result)
        setData(aggregatedData)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/client_movement_report') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const { totalJoined, averageJoined, totalLeft, averageLeft } = useMemo(() => {
    let totalJoinedLocal = 0
    let joinedNonZeroCount = 0
    let totalLeftLocal = 0
    let leftNonZeroCount = 0

    Object.values(data || {}).forEach(({ joined, left }) => {
      totalJoinedLocal += joined
      if (joined !== 0) joinedNonZeroCount++

      totalLeftLocal += left
      if (left !== 0) leftNonZeroCount++
    })

    return {
      totalJoined: totalJoinedLocal,
      averageJoined: joinedNonZeroCount
        ? Math.round(totalJoinedLocal / joinedNonZeroCount)
        : 0,
      totalLeft: totalLeftLocal,
      averageLeft: leftNonZeroCount
        ? Math.round(totalLeftLocal / leftNonZeroCount)
        : 0,
    }
  }, [data])

  const downloadExcel = (excelDataObj) => {
    import('xlsx-js-style').then((XLSX) => {
      const headRow = [
        [
          { v: 'Дата', ...ExcelRowsParams.headerRowParam },
          { v: 'На начало дня', ...ExcelRowsParams.headerRowParam },
          { v: 'Прибыло', ...ExcelRowsParams.headerRowParam },
          { v: 'Убыло', ...ExcelRowsParams.headerRowParam },
          { v: 'На конец дня', ...ExcelRowsParams.headerRowParam },
        ],
      ]

      // ИТОГО
      const totalRow = [
        { v: 'ИТОГО', ...ExcelRowsParams.tableRowParam.cell },
        { v: '-', ...ExcelRowsParams.tableRowParam.cell },
        {
          v: `Всего: ${totalJoined}, Среднее: ${averageJoined}`,
          ...ExcelRowsParams.tableRowParam.cell,
        },
        {
          v: `Всего: ${totalLeft}, Среднее: ${averageLeft}`,
          ...ExcelRowsParams.tableRowParam.cell,
        },
        { v: '-', ...ExcelRowsParams.tableRowParam.cell },
      ]

      const bodyRows = Object.entries(excelDataObj).map(
        ([date, { at_start, joined, left }]) => [
          {
            v: timeConverter(new Date(date), 'dd.mm.yyyy'),
            ...ExcelRowsParams.tableRowParam.cell,
          },
          { v: at_start, ...ExcelRowsParams.tableRowParam.cell },
          { v: joined, ...ExcelRowsParams.tableRowParam.cell },
          { v: left, ...ExcelRowsParams.tableRowParam.cell },
          {
            v: at_start + joined - left,
            ...ExcelRowsParams.tableRowParam.cell,
          },
        ],
      )

      const excelData = [...headRow, totalRow, ...bodyRows]

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(excelData)

      const wscols = [
        { wch: 20 }, // Дата
        { wch: 15 }, // На начало дня
        { wch: 15 }, // Прибыло
        { wch: 15 }, // Убыло
        { wch: 15 }, // На конец дня
      ]

      ws['!cols'] = wscols

      XLSX.utils.book_append_sheet(wb, ws, 'List 1')
      XLSX.writeFile(wb, 'Наполняемость клиентов.xlsx')
    })
  }

  return (
    <>
      <ContentHeader title='Отчет по движению клиентов' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
      </div>

      {isOpenFilter && (
        <AsideFilter
          translate={translate}
          getFilterParams={updateFilter}
          config={filterCnf}
          filterValue={filter}
          onDropFilter={() => {
            updateFilter({})
            closeFilter()
          }}
        />
      )}

      {isLoading ? (
        <Loader />
      ) : data ? (
        <>
          <div style={{ padding: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div className={styles.totalConversion} ref={refWrapper}>
            <table className={`${st.table} ${st.tbodyStriped}`}>
              <thead
                className={`${st.conversionTbl} ${styles.thead}`}
                ref={refThead}
              >
                <tr>
                  {cols.map((col, index) => (
                    <td
                      key={index}
                      className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle}`}
                    >
                      {col.name}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody className={st.conversionTbl}>
                {/* ИТОГО */}
                <tr className={st.rowOverall}>
                  <td
                    className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle} ${st.textWhite}`}
                  >
                    ИТОГО
                  </td>
                  <td
                    className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle} ${st.textWhite}`}
                  >
                    —
                  </td>
                  <td
                    className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle} ${st.textWhite}`}
                  >
                    Всего: {totalJoined}, Среднее: {averageJoined}
                  </td>
                  <td
                    className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle} ${st.textWhite}`}
                  >
                    Всего: {totalLeft}, Среднее: {averageLeft}
                  </td>
                  <td
                    className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle} ${st.textWhite}`}
                  >
                    —
                  </td>
                </tr>

                {/* Основные данные */}
                {Object.entries(data).map(
                  ([date, { at_start, joined, left }]) => (
                    <tr key={date}>
                      <td
                        className={`${st.tdTotal} ${st.textAlignCenter} ${st.tdMiddle}`}
                      >
                        {timeConverter(new Date(date), 'dd.mm.yyyy')}
                      </td>
                      <td
                        className={`${st.tdTotal} ${st.bdrN} ${st.textAlignCenter} ${st.tdMiddle} ${st.bdlN}`}
                      >
                        {at_start}
                      </td>
                      <td
                        className={`${st.tdTotal} ${st.bdrN} ${st.textAlignCenter} ${st.tdMiddle}`}
                      >
                        {joined}
                      </td>
                      <td
                        className={`${st.tdTotal} ${st.bdrN} ${st.textAlignCenter} ${st.tdMiddle}`}
                      >
                        {left}
                      </td>
                      <td
                        className={`${st.tdTotal} ${st.bdrN} ${st.textAlignCenter} ${st.tdMiddle}`}
                      >
                        {at_start + joined - left}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(CustomerTrafficReport)
